import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Tooltip } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, CloseOutlined, WarningOutlined, SettingOutlined, SyncOutlined } from '@ant-design/icons';
import { useGetPortalProjectQuery, useGetDataSourcesQuery } from '../../redux/api/compassPortal';
import { useGetIfUserIsAdminQuery } from '../../redux/api/user';
import ProjectSettingsModal from './modals/ProjectSettingsModal';

const stageRouteMap = {
    'data-collection': 'data-collection',
    'data-processing': 'data-processing',
    'data-validation': 'data-validation',
    'data-modelling': 'data-modelling',
    'compass-delivery': 'compass-delivery',
};


const defaultDescriptions = {
    'data-collection': 'All required data sources are gathered, uploaded, and tracked for completeness.',
    'data-processing': 'Raw data is cleaned, transformed, and structured ready for validation and modelling.',
    'data-validation': 'Processed data is rigorously checked for accuracy, consistency, and completeness.',
    'data-modelling': 'Our team builds and calibrates the Compass model using your validated data.',
    'compass-delivery': 'The finalised Compass model and outputs are delivered and walked through with your team.',
};

const getDynamicDescription = (stageId, stageStatus, total, collected, processed) => {
    if (total === 0) return defaultDescriptions[stageId];
    if (stageId === 'data-collection') {
        if (stageStatus === 'complete' || collected === total) return `All ${total} sources collected. Processing has begun.`;
        if (collected > 0) return `${collected} of ${total} sources collected. Upload remaining files to proceed.`;
        return 'Upload your data files and mark them as collected to begin.';
    }
    if (stageId === 'data-processing') {
        if (stageStatus === 'complete' || processed === collected) return `All ${collected} sources processed. Moving to validation.`;
        if (processed > 0) return `${processed} of ${collected} sources processed by the team.`;
        return 'Our team will review and process your collected data.';
    }
    return defaultDescriptions[stageId];
};

const ProjectHome = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [showSettings, setShowSettings] = useState(false);

    const { data: project, isLoading } = useGetPortalProjectQuery({ projectId });
    const { data: sources } = useGetDataSourcesQuery({ projectId });
    const { data: isAdmin } = useGetIfUserIsAdminQuery();

    const totalSources = (sources || []).length;
    const collectedCount = (sources || []).filter(s => s.collected).length;
    const processedCount = (sources || []).filter(s => s.processed).length;

    const handleStageClick = (stage) => {
        if (!stageRouteMap[stage.id]) return;
        const route = stageRouteMap[stage.id];
        if (route) navigate(`/compass-portal/${projectId}/${route}`);
    };

    if (isLoading) {
        return <div style={{ width: '100%', textAlign: 'center', padding: '80px 0' }}><Spin size="large" /></div>;
    }
    if (!project) return null;

    const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

    return (
        <div style={{ width: '100%' }}>
            <button className="cp-back-link" onClick={() => navigate('/compass-portal')}>
                <ArrowLeftOutlined /> Back to projects
            </button>

            <div className="cp-project-header">
                <div className="cp-project-header__field">
                    <span className="cp-project-header__label">PROJECT NAME</span>
                    <span className="cp-project-header__value">{project.project_name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div className="cp-project-header__right">
                        <div className="cp-project-header__dateline">
                            <span className="cp-project-header__label">TIMELINES</span>
                            <span className="cp-project-header__date">{project.kickoff_date || '—'}</span>
                            <span className="cp-project-header__dash">—</span>
                            <span className="cp-project-header__date">{project.delivery_date || '—'}</span>
                        </div>
                        <div className="cp-project-header__dateline">
                            <span className="cp-project-header__label">CURRENT DATE</span>
                            <span className="cp-project-header__date">{today}</span>
                        </div>
                    </div>
                    {isAdmin && (
                        <button className="cp-action-btn" onClick={() => setShowSettings(true)}>
                            <SettingOutlined /> Settings
                        </button>
                    )}
                </div>
            </div>

            <div className="cp-timeline-wrapper">
                <div className="cp-timeline">
                    <div className="cp-timeline__line" />
                    {(project.stages || []).map((stage) => {
                        const isClickable = !!stageRouteMap[stage.id];
                        return (
                            <div
                                key={stage.id}
                                className="cp-timeline-stage"
                                onClick={() => isClickable && handleStageClick(stage)}
                                style={{ cursor: isClickable ? 'pointer' : 'default' }}
                            >
                                <div className="cp-timeline-stage__top">
                                    <div className={`cp-timeline-stage__alert ${stage.status === 'blocked' ? 'cp-timeline-stage__alert--visible' : ''}`}>
                                        <WarningOutlined />
                                    </div>
                                    <div className="cp-timeline-stage__name">{stage.name}</div>
                                </div>
                                <Tooltip title={
                                    stage.status === 'complete' ? 'Complete' :
                                    stage.status === 'in-progress' ? 'In Progress' :
                                    stage.status === 'blocked' ? 'Blocked' : 'Not Started'
                                }>
                                    <div className={`cp-timeline-stage__node cp-timeline-stage__node--${stage.status || 'not-started'}`}>
                                        {stage.status === 'complete' && <CheckOutlined />}
                                        {stage.status === 'in-progress' && <SyncOutlined spin style={{ color: '#f59e0b' }} />}
                                        {stage.status === 'blocked' && <CloseOutlined />}
                                    </div>
                                </Tooltip>
                                <div className="cp-timeline-stage__below">
                                    {/* Progress count */}
                                    {stage.id === 'data-collection' && totalSources > 0 && (
                                        <div style={{ fontSize: 12, fontWeight: 600, color: collectedCount === totalSources ? 'var(--green-color)' : 'var(--medium-light-grey-color)', marginBottom: 4 }}>
                                            {collectedCount}/{totalSources} collected
                                        </div>
                                    )}
                                    {stage.id === 'data-processing' && collectedCount > 0 && (
                                        <div style={{ fontSize: 12, fontWeight: 600, color: processedCount === collectedCount ? 'var(--green-color)' : 'var(--medium-light-grey-color)', marginBottom: 4 }}>
                                            {processedCount}/{collectedCount} processed
                                        </div>
                                    )}
                                    <div className="cp-timeline-stage__desc">
                                        {getDynamicDescription(stage.id, stage.status, totalSources, collectedCount, processedCount)}
                                    </div>
                                    {stage.due_date && (() => {
                                        const isOverdue = stage.status !== 'complete' && new Date(stage.due_date) < new Date();
                                        return (
                                            <div className="cp-timeline-stage__due" style={isOverdue ? { color: 'var(--red-color, #d3281f)', fontWeight: 600 } : {}}>
                                                {isOverdue ? 'Overdue: ' : ''}{stage.due_date}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <ProjectSettingsModal
                open={showSettings}
                onClose={() => setShowSettings(false)}
                project={project}
            />
        </div>
    );
};

export default ProjectHome;
